import { useEffect, useState } from "react";
import { MiInputMultiSelectBox, MiModal } from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";

/**
 * A modal component for managing milestone selections in a build template.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls the visibility of the modal
 * @param {Object} props.buildTemplate - The build template object containing existing milestone selections
 * @param {Array} props.milestones - List of available milestones to select from
 * @param {boolean} props.loading - Indicates if the component is in a loading state
 * @param {Function} props.onModalClose - Callback function when modal is closed
 * @param {Function} props.onSubmit - Callback function when form is submitted
 * @returns {JSX.Element} The rendered modal component
 *
 * @example
 * <ManageMilestonesModal
 *   open={isModalOpen}
 *   buildTemplate={currentBuildTemplate}
 *   milestones={availableMilestones}
 *   loading={isLoading}
 *   onModalClose={handleCloseModal}
 *   onSubmit={handleSubmit}
 * />
 */
export const ManageMilestonesModal = ({
  open,
  buildTemplate,
  milestones,
  loading,
  onModalClose,
  onSubmit,
}) => {
  const [selectedMilestones, setSelectedMilestones] = useState([]);

  const handleSubmit = () => {
    const { milestones: _milestones, ...rest } = buildTemplate;
    onSubmit({
      ...rest,
      milestoneIds: selectedMilestones,
    });
  };

  useEffect(() => {
    setSelectedMilestones(buildTemplate?.milestones?.map(({ id }) => id) || []);
  }, [buildTemplate, setSelectedMilestones]);

  return (
    <MiModal
      title="Manage Products"
      open={open}
      loading={loading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: onModalClose,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          disabled: loading,
          onClick: handleSubmit,
        },
      ]}
      closeModal={onModalClose}
    >
      <MiInputMultiSelectBox
        disableNone
        labelText="Milestones"
        value={selectedMilestones}
        menuItems={
          milestones.map((orderType) => ({
            label: orderType.name,
            value: orderType.id,
          })) || []
        }
        handleChange={(event) => setSelectedMilestones(event.target.value)}
      />
    </MiModal>
  );
};
